import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Icon, Metric, viLabel } from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { AuditTimeline } from '@/components/review/AuditTimeline';
import { BarChart, type BarData } from '@/components/charts/BarChart';
import { useAuth } from '@/lib/auth';
import { useScope } from '@/lib/scope';
import { useViewport } from '@/lib/viewport';
import { useOnboardingState } from '@/lib/onboarding';
import { getOnboardingFor } from '@/content/onboarding';
import { useMockTable } from '@/hooks/useMockTable';
import { useForecastResults } from '@/hooks/useForecast';
import { formatNumber, toISODate } from '@/lib/utils';

function greetingNow(): string {
  const h = new Date().getHours();
  if (h < 11) return 'Chào buổi sáng';
  if (h < 14) return 'Chào buổi trưa';
  if (h < 18) return 'Chào buổi chiều';
  return 'Chào buổi tối';
}

const HORIZON_DAYS = 14;

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentWarehouse, role } = useScope();
  const { isMobile, vw } = useViewport();
  const { state: onboarding, update: updateOnboarding } = useOnboardingState();
  const roleContent = useMemo(() => getOnboardingFor(role), [role]);

  if (!onboarding.completedAt) {
    return <Navigate to="/welcome" replace />;
  }

  const roleChanged =
    onboarding.lastSeenRole !== null && onboarding.lastSeenRole !== role;

  const customers = useMockTable('customers');
  const historical = useMockTable('historical_orders');
  const adjustments = useMockTable('forecast_adjustments');
  const calendar = useMockTable('day_calendar');
  const audit = useMockTable('audit_log');
  const forecast = useForecastResults({ warehouseId: currentWarehouse?.id });

  const today = toISODate(new Date());
  const horizonEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + HORIZON_DAYS - 1);
    return toISODate(d);
  }, [today]);
  const todayCal = calendar.find((d) => d.date === today);

  const horizonForecast = useMemo(
    () => forecast.filter((r) => r.date >= today && r.date <= horizonEnd),
    [forecast, today, horizonEnd],
  );

  const histThisWh = currentWarehouse
    ? historical.filter((h) => h.warehouse_id === currentWarehouse.id)
    : historical;
  const avgDaily =
    histThisWh.length > 0
      ? Math.round(histThisWh.reduce((s, h) => s + h.qty, 0) / 90)
      : 0;
  const pendingAdj = adjustments.filter((a) => a.state === 'NEW').length;
  const acceptedAdj = adjustments.filter((a) => a.state === 'ACCEPTED').length;

  const forecastQty = horizonForecast.reduce((s, r) => s + r.forecast_qty, 0);
  const forecastHours = horizonForecast.reduce((s, r) => s + r.total_workload_hours, 0);
  const lowConfRate =
    horizonForecast.length > 0
      ? Math.round(
          (horizonForecast.filter((r) => r.confidence.is_low_confidence).length /
            horizonForecast.length) *
            100,
        )
      : 0;

  const firstName = (user?.displayName || user?.email || 'bạn').split(' ').slice(-1)[0];

  // Daily chart from horizon forecast
  const chartData: BarData[] = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of horizonForecast) {
      m.set(r.date, (m.get(r.date) ?? 0) + r.forecast_qty);
    }
    return Array.from(m.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, qty]) => {
        const cal = calendar.find((c) => c.date === date);
        return {
          label: date.slice(8),
          value: qty,
          tone:
            cal?.event_type === 'MEGA_SALE' || cal?.event_type === 'DOUBLE_DAY'
              ? 'critical'
              : ['PAYDAY', 'MID_MONTH', 'MINI_SPIKE'].includes(cal?.event_type ?? '')
                ? 'warning'
                : 'default',
          highlight: date === today,
        };
      });
  }, [horizonForecast, calendar, today]);

  const hasForecast = horizonForecast.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Trang chủ']}
        title={`${greetingNow()}, ${firstName}`}
        subtitle={
          currentWarehouse
            ? `Hôm nay ${today} · ${currentWarehouse.code} · loại ngày ${viLabel(todayCal?.event_type ?? 'NORMAL')}`
            : `Hôm nay ${today}`
        }
        actions={
          <>
            {!isMobile && (
              <Button variant="outline" icon="refresh" onClick={() => window.location.reload()}>
                Làm mới
              </Button>
            )}
            <Button
              variant="primary"
              icon="forecast"
              onClick={() => navigate('/planner/forecast')}
            >
              {isMobile ? 'Mở dự báo' : 'Mở dự báo hôm nay'}
            </Button>
          </>
        }
      />
      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        {roleChanged && (
          <div
            style={{
              marginBottom: 14,
              padding: '10px 14px',
              background: '#eef2ff',
              border: '1px solid var(--bx-primary)',
              borderRadius: 'var(--bx-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <Icon name="info" size={14} style={{ color: 'var(--bx-primary)' }} />
            <span style={{ fontSize: 13, color: 'var(--bx-text-body)', flex: 1, minWidth: 200 }}>
              Bạn vừa đổi sang vai trò <strong>{roleContent.roleLabel}</strong> — xem hướng dẫn nhanh trong 1 phút?
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateOnboarding({ lastSeenRole: role })}
              >
                Bỏ qua
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon="book"
                onClick={() => navigate('/welcome')}
              >
                Xem hướng dẫn
              </Button>
            </div>
          </div>
        )}
        {/* KPI row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: vw < 560 ? 'repeat(2, 1fr)' : vw < 900 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? 10 : 16,
          }}
        >
          <Metric
            label={hasForecast ? `Dự báo ${HORIZON_DAYS}N` : 'Trung bình đơn / ngày'}
            value={formatNumber(hasForecast ? forecastQty : avgDaily)}
            sublabel={hasForecast ? 'đơn forecast' : 'đơn · 90 ngày qua'}
            hint={currentWarehouse ? `Kho ${currentWarehouse.code}` : 'Chưa chọn kho'}
          />
          <Metric
            label={hasForecast ? 'Workload tổng' : 'Khách hàng hoạt động'}
            value={
              hasForecast
                ? `${formatNumber(Math.round(forecastHours))}h`
                : customers.filter((c) => c.is_active).length
            }
            sublabel={hasForecast ? `≈ ${(forecastHours / 8).toFixed(1)} ca` : 'customers'}
            deltaTone={hasForecast ? 'flat' : 'up'}
          />
          <Metric
            label="Điều chỉnh chờ duyệt"
            value={pendingAdj}
            sublabel={`${acceptedAdj} đã chốt`}
            delta={pendingAdj > 0 ? 'Cần phê duyệt' : 'Trống hàng đợi'}
            deltaTone={pendingAdj > 0 ? 'down' : 'flat'}
          />
          <Metric
            label={hasForecast ? 'Độ tin cậy' : 'Đơn lịch sử'}
            value={hasForecast ? `${100 - lowConfRate}%` : formatNumber(historical.length)}
            sublabel={
              hasForecast
                ? `${lowConfRate}% low-conf`
                : 'rows toàn hệ thống'
            }
            deltaTone={hasForecast && lowConfRate > 30 ? 'down' : 'flat'}
          />
        </div>

        {/* Forecast chart */}
        {hasForecast && (
          <Card
            title={`Dự báo ${HORIZON_DAYS} ngày tới`}
            subtitle={`${horizonForecast.length} rows từ engine M4 · ${today} → ${horizonEnd}`}
            style={{ marginTop: 16 }}
            actions={
              <Button
                variant="ghost-primary"
                size="sm"
                iconRight="arrowRight"
                onClick={() => navigate('/planner/forecast')}
              >
                Chi tiết
              </Button>
            }
          >
            <BarChart data={chartData} height={140} />
          </Card>
        )}

        {/* 2-column: status + audit timeline */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: vw < 1100 ? '1fr' : '2fr 1fr',
            gap: isMobile ? 12 : 16,
            marginTop: isMobile ? 12 : 16,
          }}
        >
          <Card
            title="Tổng quan kho hiện hành"
            subtitle={
              currentWarehouse
                ? `${currentWarehouse.name} · capacity ${formatNumber(currentWarehouse.capacity_orders_per_day)} đơn/ngày`
                : 'Chọn kho ở thanh trên'
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <KV label="Quốc gia" value={currentWarehouse?.country ?? '—'} />
              <KV label="Vùng" value={currentWarehouse?.region ?? '—'} />
              <KV
                label="Trạng thái"
                value={
                  <Badge kind={currentWarehouse?.is_active ? 'ACTIVE' : 'INACTIVE'}>
                    {currentWarehouse?.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </Badge>
                }
              />
              <KV
                label="Đơn 90 ngày"
                mono
                value={`${formatNumber(histThisWh.reduce((s, h) => s + h.qty, 0))} đơn`}
              />
              <KV
                label="Trung bình/ngày"
                mono
                value={`${formatNumber(avgDaily)} đơn`}
              />
              <KV
                label="Sự kiện hôm nay"
                value={
                  todayCal ? (
                    <Badge
                      kind={
                        todayCal.event_type === 'NORMAL'
                          ? 'DRAFT'
                          : todayCal.event_type === 'MEGA_SALE'
                            ? 'CRITICAL'
                            : 'WARNING'
                      }
                    >
                      {viLabel(todayCal.event_type)}
                    </Badge>
                  ) : (
                    <Badge>—</Badge>
                  )
                }
              />
            </div>
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid var(--bx-border-muted)',
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outline"
                size="sm"
                icon="eye"
                onClick={() => navigate('/admin/warehouses')}
              >
                Xem tất cả kho
              </Button>
              <Button
                variant="ghost-primary"
                size="sm"
                icon="upload"
                onClick={() => navigate('/admin/history-import')}
              >
                Import lịch sử
              </Button>
              <Button
                variant="ghost-primary"
                size="sm"
                icon="check"
                onClick={() => navigate('/planner/forecast/review')}
              >
                Phê duyệt ({pendingAdj})
              </Button>
            </div>
          </Card>

          <AuditTimeline
            entries={audit.slice(0, 8)}
            title="Hoạt động gần đây"
            emptyText="Chưa có audit log"
          />
        </div>
      </div>
    </div>
  );
}

function KV({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          fontSize: 11.5,
          color: 'var(--bx-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 600,
          width: 130,
          flexShrink: 0,
        }}
      >
        {label}
      </div>
      <div
        style={{
          flex: 1,
          fontSize: 13,
          color: 'var(--bx-text-dark)',
          fontFamily: mono ? 'var(--bx-font-mono)' : 'inherit',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
    </div>
  );
}
